import React, { useState, useContext, useEffect } from 'react';
import { ThemeContext } from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUnreadErrorAlertNotificationsAmount,
  signOut,
} from '../../services';
import { APP_VERSION_LABEL } from '../../utils';
import logoNexus from '../../assets/images/logo_nexus_small.png';
import ConnectionStatus from '../connection-status';
import Icon from '../icon/icon';
import * as s from './styled-side-menu';

const SideMenu = ({ handleChange }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { amount } = useSelector((state) => state.notifications);
  const { user } = useSelector((state) => state.usuario);
  // Contexto atual em Redux (persistido). Usado pra exibir atalhos
  // "Voltar pra Cliente X / Parque Y" quando o operador saiu pra
  // outra rota (ex.: Notificacoes) e quer voltar rapido.
  const clienteSelecionado = useSelector(
    (state) => state.segundoNivel?.clienteSelecionado
  );
  const parqueSelecionado = useSelector(
    (state) => state.segundoNivel?.parqueSelecionado
  );
  const [hover, setHover] = useState('');
  const [isOpen, setIsOpen] = useState();
  const selected = location.pathname.split('/')[1];
  const { colors, title } = useContext(ThemeContext);
  const hasClienteContext =
    clienteSelecionado && clienteSelecionado.id && clienteSelecionado.name;
  const hasParqueContext =
    parqueSelecionado && parqueSelecionado.id && parqueSelecionado.name;
  // Em /dashboard/customer o operador ja esta no contexto — esconde
  // o atalho pra evitar redundancia.
  const onClienteRoute = location.pathname.startsWith('/dashboard/customer');
  const showContextSection =
    !onClienteRoute && (hasClienteContext || hasParqueContext);

  const role = (user.role || '').toString().trim().toLowerCase();

  useEffect(() => {
    let mounted = true;

    const syncUnreadNotifications = () => {
      getUnreadErrorAlertNotificationsAmount()
        .then((unreadAmount) => {
          if (!mounted) return;
          dispatch({
            type: 'NOTIFICATION/SET_NOTIFICATION_AMOUNT',
            amount: unreadAmount,
          });
        })
        .catch(() => {});
    };

    syncUnreadNotifications();
    const interval = setInterval(syncUnreadNotifications, 60000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [dispatch]);

  const bubbleCount = amount;

  return (
    <s.Container
      open={isOpen}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setTimeout(() => setIsOpen(false), 200)}
    >
      {/* Header: quando aberto, avatar+user+toggle inline numa unica
          row com divider abaixo. Quando colapsado, so a ToggleCircle
          centralizada no topo. Theme toggle removida do menu conforme
          design — handleChange permanece prop pra compat futura. */}
      {isOpen ? (
        <s.HeaderRow>
          <s.AvatarSlot
            onMouseEnter={() => setHover('avatar')}
            onMouseLeave={() => setHover('')}
          >
            <Icon name="user" size={22} strokeWidth={1.6} />
          </s.AvatarSlot>
          <s.UserContainer className="fade-in">
            <s.SidemenuLabel hover={hover === 'avatar'}>
              Olá {user.name}
            </s.SidemenuLabel>
            <s.SidemenuLabel
              size="10px"
              color={hover === 'avatar' ? colors.white : colors.label}
              onClick={async () => {
                try {
                  await signOut();
                  dispatch({ type: 'USUARIO/SET_USER', user: null });
                  dispatch({ type: 'SEGUNDO/RESET' });
                } catch (e) {
                  // opcional: exibir feedback ao usuário
                }
              }}
              underline
            >
              SAIR
            </s.SidemenuLabel>
          </s.UserContainer>
          <s.ToggleCircle
            type="button"
            onClick={() => setIsOpen(false)}
            title="Recolher menu"
            aria-label="Recolher menu"
          >
            <Icon name="chevron-left" size={18} />
          </s.ToggleCircle>
        </s.HeaderRow>
      ) : (
        <s.Options>
          <s.OptionWrapper noHover open={false} play $divider>
            <s.ToggleCircle
              type="button"
              onClick={() => setIsOpen(true)}
              title="Expandir menu"
              aria-label="Expandir menu"
            >
              <Icon name="chevron-right" size={18} />
            </s.ToggleCircle>
          </s.OptionWrapper>
        </s.Options>
      )}

      {/* Atalhos contextuais: aparecem so com menu expandido + contexto
          presente no Redux + nao estamos na rota /dashboard/customer
          (evita redundancia). Operador que abriu Notificacoes / Eventos
          pode voltar em 1 clique pra Parque/Cliente que estava
          monitorando. */}
      {showContextSection && isOpen && (
        <s.ContextSection>
          {hasParqueContext && (
            <s.ContextItem
              type="button"
              title={`Voltar para ${parqueSelecionado.name}`}
              onClick={() =>
                history.push('/dashboard/customer', { ...parqueSelecionado })
              }
            >
              {/* lucide:map-pin */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <s.ContextLabel>
                <small>Parque</small>
                <strong>{parqueSelecionado.name}</strong>
              </s.ContextLabel>
            </s.ContextItem>
          )}
          {hasClienteContext && !hasParqueContext && (
            <s.ContextItem
              type="button"
              title={`Voltar para ${clienteSelecionado.name}`}
              onClick={() =>
                history.push('/dashboard/customer', { ...clienteSelecionado })
              }
            >
              {/* lucide:building */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <path d="M9 22v-4h6v4" />
              </svg>
              <s.ContextLabel>
                <small>Cliente</small>
                <strong>{clienteSelecionado.name}</strong>
              </s.ContextLabel>
            </s.ContextItem>
          )}
        </s.ContextSection>
      )}

      <s.Options>
        <s.OptionWrapper
          title="Dashboard"
          onMouseEnter={() => setHover('dashboard')}
          onMouseLeave={() => setHover('')}
          open={isOpen}
          $selected={selected === 'dashboard'}
          justify="start"
          onClick={() => {
            dispatch({
              type: 'SEGUNDO/RESET',
            });
            dispatch({
              type: 'APLICACAO/RESET',
            });
            history.push('/dashboard');
          }}
        >
          <s.IconSlot $selected={selected === 'dashboard'}>
            <Icon name="layout-dashboard" size={22} />
          </s.IconSlot>
          <s.SidemenuLabel
            className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
            hover={hover === 'dashboard'}
          >
            Dashboard
          </s.SidemenuLabel>
        </s.OptionWrapper>

        <s.OptionWrapper
          title="Relatórios"
          onMouseEnter={() => setHover('reports')}
          onMouseLeave={() => setHover('')}
          open={isOpen}
          $selected={selected === 'reports'}
          justify="start"
          onClick={() => history.push('/reports')}
        >
          <s.IconSlot $selected={selected === 'reports'}>
            <Icon name="bar-chart-3" size={22} />
          </s.IconSlot>

          <s.SidemenuLabel
            className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
            hover={hover === 'reports'}
          >
            Relatórios
          </s.SidemenuLabel>
        </s.OptionWrapper>

        {role === 'higra' && (
          <s.OptionWrapper
            title="Clientes"
            onMouseEnter={() => setHover('customers')}
            onMouseLeave={() => setHover('')}
            open={isOpen}
            $selected={selected === 'customers'}
            justify="start"
            onClick={() => history.push('/customers')}
          >
            <s.IconSlot $selected={selected === 'customers'}>
              <Icon name="users" size={22} />
            </s.IconSlot>

            <s.SidemenuLabel
              className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
              hover={hover === 'customers'}
            >
              Clientes
            </s.SidemenuLabel>
          </s.OptionWrapper>
        )}
        {(role === 'higra' || role === 'admin') && (
          <s.OptionWrapper
            title="Usuários"
            onMouseEnter={() => setHover('users')}
            onMouseLeave={() => setHover('')}
            open={isOpen}
            $selected={selected === 'users'}
            justify="start"
            onClick={() => history.push('/users')}
          >
            <s.IconSlot $selected={selected === 'users'}>
              <Icon name="user-check" size={22} />
            </s.IconSlot>

            <s.SidemenuLabel
              className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
              hover={hover === 'users'}
            >
              Usuários
            </s.SidemenuLabel>
          </s.OptionWrapper>
        )}
        {role === 'higra' && (
          <s.OptionWrapper
            title="Estações"
            onMouseEnter={() => setHover('stations')}
            onMouseLeave={() => setHover('')}
            open={isOpen}
            $selected={selected === 'stations'}
            justify="start"
            onClick={() => history.push('/stations')}
          >
            <s.IconSlot $selected={selected === 'stations'}>
              <Icon name="factory" size={22} />
            </s.IconSlot>

            <s.SidemenuLabel
              className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
              hover={hover === 'stations'}
            >
              Estações
            </s.SidemenuLabel>
          </s.OptionWrapper>
        )}
        {role === 'higra' && (
          <s.OptionWrapper
            title="Máquinas"
            onMouseEnter={() => setHover('machines')}
            onMouseLeave={() => setHover('')}
            open={isOpen}
            $selected={selected === 'machines'}
            justify="start"
            onClick={() => history.push('/machines')}
          >
            <s.IconSlot $selected={selected === 'machines'}>
              <Icon name="cpu" size={22} />
            </s.IconSlot>

            <s.SidemenuLabel
              className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
              hover={hover === 'machines'}
            >
              Máquinas
            </s.SidemenuLabel>
          </s.OptionWrapper>
        )}
        {role === 'higra' && (
          <s.OptionWrapper
            title="Variáveis"
            onMouseEnter={() => setHover('variables')}
            onMouseLeave={() => setHover('')}
            open={isOpen}
            $selected={selected === 'variables'}
            justify="start"
            onClick={() => history.push('/variables')}
          >
            <s.IconSlot $selected={selected === 'variables'}>
              <Icon name="settings" size={22} />
            </s.IconSlot>

            <s.SidemenuLabel
              className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
              hover={hover === 'variables'}
            >
              Variáveis
            </s.SidemenuLabel>
          </s.OptionWrapper>
        )}
        {role === 'higra' && (
          <s.OptionWrapper
            title="Logs de alteração"
            onMouseEnter={() => setHover('audit-logs')}
            onMouseLeave={() => setHover('')}
            open={isOpen}
            $selected={selected === 'audit-logs'}
            justify="start"
            onClick={() => history.push('/audit-logs')}
          >
            <s.IconSlot $selected={selected === 'audit-logs'}>
              <Icon name="history" size={22} />
            </s.IconSlot>

            <s.SidemenuLabel
              className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
              hover={hover === 'audit-logs'}
            >
              Logs
            </s.SidemenuLabel>
          </s.OptionWrapper>
        )}
        <s.OptionWrapper
          title="Notificações"
          onMouseEnter={() => setHover('notifications')}
          onMouseLeave={() => setHover('')}
          open={isOpen}
          $selected={selected === 'notifications'}
          justify="start"
          onClick={() => history.push('/notifications')}
        >
          <s.IconSlot $selected={selected === 'notifications'}>
            <Icon name="bell" size={22} />
            {!!bubbleCount && (
              <s.BubbleNotifications
                title={`${bubbleCount} notificação(ões) pendente(s)`}
              >
                {bubbleCount}
              </s.BubbleNotifications>
            )}
          </s.IconSlot>
          <s.SidemenuLabel
            className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
            hover={hover === 'notifications'}
          >
            Notificações
          </s.SidemenuLabel>
        </s.OptionWrapper>

        {/* Toggle de tema (sun/moon) — sempre visivel em ambos estados
            do menu. Quando colapsado, mostra so o icone centralizado;
            quando aberto, icone + label "Modo claro/escuro". onClick
            chama handleChange do parent (alterna theme.title). */}
        <s.OptionWrapper
          title={
            title === 'dark'
              ? 'Mudar para modo claro'
              : 'Mudar para modo escuro'
          }
          onMouseEnter={() => setHover('theme')}
          onMouseLeave={() => setHover('')}
          open={isOpen}
          justify="start"
          onClick={handleChange}
        >
          <s.IconSlot>
            <Icon name={title === 'dark' ? 'sun' : 'moon'} size={20} />
          </s.IconSlot>
          <s.SidemenuLabel
            className={isOpen ? 'fade-in' : isOpen === false && 'fade-out'}
            hover={hover === 'theme'}
          >
            {title === 'dark' ? 'Modo claro' : 'Modo escuro'}
          </s.SidemenuLabel>
        </s.OptionWrapper>
      </s.Options>
      <s.FooterStack>
        <ConnectionStatus variant="sidebar" open={!!isOpen} />
        {/* Logo Nexus — horizontal quando o sidebar esta aberto,
            rotacionado 90deg (vertical bottom-up) quando colapsado.
            Mesma imagem em ambos os modos pra consistencia visual. */}
        <s.HigraMark $horizontal={!!isOpen} aria-label="Nexus">
          <img src={logoNexus} alt="" />
        </s.HigraMark>
        {isOpen && (
          <s.VersionLabel
            type="button"
            title="Ver changelog"
            onClick={() => history.push('/changelog')}
          >
            {APP_VERSION_LABEL}
          </s.VersionLabel>
        )}
      </s.FooterStack>
    </s.Container>
  );
};

export default SideMenu;
